<template>
  <v-skeleton-loader
    :loading="loading"
    type="table-heading, list-item-avatar-two-line@5"
  >
    <v-data-table
      :headers="headers"
      :header-props="{ sortByText: $t('sortBy') }"
      :items="customers"
      :single-expand="true"
      :expanded.sync="expanded"
      item-key="_id"
      sort-by="ordersCount"
      :sort-desc="true"
      show-expand
      :search="searchValue"
      :custom-filter="customFilter"
      disable-pagination
      hide-default-footer
      @click:row="
        (item, slot) =>
          expanded.length === 1 && expanded[0]._id === item._id
            ? (expanded = [])
            : (expanded = [item])
      "
    >
      <template v-slot:top>
        <v-toolbar flat color="white">
          <div class="flex-grow-1"></div>
          <v-dialog v-model="dialog" max-width="500" persistent>
            <template v-slot:activator="{ on }">
              <v-btn color="primary" text rounded class="mb-2" v-on="on"
                >{{ $t("loyaltyProgram") }}
                <v-icon right dark>mdi-diamond-stone</v-icon></v-btn
              >
            </template>
            <v-card>
              <v-card-title class="headline mb-2">
                {{ $t("loyaltyProgram") }}
              </v-card-title>
              <v-card-subtitle>{{
                $t("loyaltyProgramDescription")
              }}</v-card-subtitle>

              <v-form ref="form" v-model="loyaltyProgramValid">
                <v-card-text class="pt-0">
                  <v-col cols="12">
                    <v-switch
                      v-model="loyaltyProgram.status"
                      class="mt-0 pt-0 d-inline"
                      :label="
                        loyaltyProgram.status
                          ? $t('loyaltyProgramEnabled')
                          : $t('loyaltyProgramDisabled')
                      "
                      color="primary"
                      hide-details
                    ></v-switch>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="loyaltyProgram.loyaltyCreditsPercentage"
                      :label="`${$t('loyaltyCreditsPercentage')} *`"
                      :hint="$t('loyaltyCreditsPercentageHint')"
                      type="number"
                      placeholder="10"
                      :rules="[
                        rules.required,
                        rules.notNegativeInteger,
                        (v) => (v >= 1 && v <= 100) || 'Max: 100',
                      ]"
                      rounded
                      filled
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="loyaltyProgram.maximumRedeemedLoyaltyCredits"
                      :label="`${$t('maximumRedeemedLoyaltyCredits')} *`"
                      :hint="
                        $t('maximumRedeemedLoyaltyCreditsHint', {
                          currency: currencySymbol,
                        })
                      "
                      placeholder="500"
                      type="number"
                      :rules="[rules.required, rules.notNegativeInteger]"
                      rounded
                      filled
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="loyaltyProgram.loyaltyCreditsValidityInDays"
                      :label="`${$t('loyaltyCreditsValidityInDays')} *`"
                      :hint="$t('loyaltyCreditsValidityInDaysHint')"
                      type="number"
                      placeholder="7"
                      :rules="[
                        rules.required,
                        rules.notNegativeInteger,
                        (v) => (v >= 5 && v <= 90) || 'Min: 5 Max: 90',
                      ]"
                      rounded
                      filled
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="loyaltyProgram.orderingUrl"
                      :label="`${$t('onlineOrderingUrl')} *`"
                      :hint="$t('onlinePaymentReturnUrlHint')"
                      :rules="[rules.required, rules.url]"
                      rounded
                      filled
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" text rounded @click="dialog = false">{{
                    $t("close")
                  }}</v-btn>
                  <v-btn
                    color="success"
                    text
                    rounded
                    :disabled="!loyaltyProgramValid"
                    @click="saveLoyaltyProgram()"
                    :loading="loyaltyProgramLoading"
                  >
                    {{ $t("save") }}
                  </v-btn>
                </v-card-actions>
              </v-form>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>
      <template v-slot:item.ordersSum="{ item }">
        {{
          item.orderHistory[0].orders.length === 1
            ? item.orderHistory[0].orders[0].total
            : item.orderHistory[0].orders
                .map((item) => item.total)
                .reduce((prev, next) => prev + next)
                .toFixed(2)
        }}
        {{ currencySymbol }}
      </template>
      <template v-slot:item.lastOrdered="{ item }">
        {{ getPrettyDate(item.lastOrdered) }}
      </template>
      <!-- <template v-slot:item.marketingConsent="{ item }">
        {{ item.orderHistory[0].consents.marketing ? $t("Yes") : $t("No") }}
      </template> -->
      <template v-slot:item.loyaltyCredits="{ item }">
        <v-icon v-if="item.orderHistory[0].loyaltyCreditsLock" small
          >mdi-lock</v-icon
        >
        {{ item.orderHistory[0].loyaltyCredits }}
      </template>
      <template v-slot:item.loyaltyCreditsValidUntil="{ item }">
        {{ getPrettyDate(item.orderHistory[0].loyaltyCreditsValidUntil) }}
      </template>
      <template v-slot:no-data>
        {{ $t("noData") }}
      </template>
      <template v-slot:no-results>
        {{ $t("noData") }}
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td id="edpandedTd" :colspan="headers.length">
          <v-data-table
            id="expandedTable"
            class="mb-5"
            :headers="orderHeaders"
            :header-props="{ sortByText: $t('sortBy') }"
            :items="item.orderHistory[0].orders"
            item-key="_id"
            sort-by="created"
            :sort-desc="true"
            hide-default-footer
            disable-pagination
            :style="$vuetify.breakpoint.xs ? 'width: 95%; margin: 0 auto' : ''"
          >
            <template v-slot:item.orderIdReadable="{ item }">
              <v-btn
                color="primary"
                depressed
                rounded
                small
                @click="
                  $router.push(`/orders?includeOrderId=${item.orderId}`);
                  $emit('search-value-change', item.orderIdReadable);
                "
              >
                {{ item.orderIdReadable }}
                <v-icon small class="ml-1">mdi-information</v-icon>
              </v-btn>
            </template>
            <template v-slot:item.created="{ item }">
              {{ getPrettyDate(item.created) }}
            </template>
            <template v-slot:item.total="{ item }">
              {{ item.total.toFixed(2) }} {{ currencySymbol }}
            </template>
            <template v-slot:no-data>
              {{ $t("noData") }}
            </template>
          </v-data-table>
        </td>
      </template>
    </v-data-table>
  </v-skeleton-loader>
</template>

<script>
import restaurantService from "../services/restaurant.service";
import customerService from "../services/customer.service";

export default {
  props: ["searchValue"],
  data() {
    return {
      loading: true,
      dialog: false,
      loyaltyProgramLoading: false,
      loyaltyProgram: {
        status: false,
        loyaltyCreditsPercentage: null,
        maximumRedeemedLoyaltyCredits: null,
        loyaltyCreditsValidityInDays: null,
        orderingUrl: null,
      },
      loyaltyProgramValid: true,
      rules: {
        required: (value) => !!value || this.$t("requiredField"),
        notNegativeInteger: (value) =>
          (Number.isInteger(Number(value)) && value >= 0) ||
          this.$t("mustBeInteger"),
        url: (v) =>
          /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/.test(
            v
          ) || this.$t("invalidUrl"),
      },
      customers: [],
      expanded: [],
      headers: [
        { text: this.$t("customerName"), value: "name", sortable: false },
        { text: this.$t("email"), value: "email", sortable: false },
        { text: this.$t("mobile"), value: "mobile", sortable: false },
        {
          text: this.$t("numberOfOrders"),
          value: "ordersCount",
        },
        { text: this.$t("ordersSum"), value: "ordersSum" },
        { text: this.$t("lastOrdered"), value: "lastOrdered" },
        // { text: this.$t("marketingConsent"), value: "marketingConsent" },
        {
          text: this.$t("loyaltyCredits"),
          value: "loyaltyCredits",
        },
        {
          text: this.$t("validUntil"),
          value: "loyaltyCreditsValidUntil",
          sortable: false,
        },
        { text: "", value: "data-table-expand" },
      ],
      orderHeaders: [
        { text: this.$t("id"), value: "orderIdReadable", sortable: false },
        { text: this.$t("created"), value: "created" },
        { text: this.$t("total"), value: "total" },
        {
          text: this.$t("redeemedLoyaltyCredits"),
          value: "redeemedLoyaltyCredits",
        },
      ],
    };
  },
  computed: {
    currencySymbol() {
      return localStorage.currencySymbol;
    },
  },
  created() {
    // fetch the data when the view is created and the data is
    // already being observed
    this.fetchData();
  },
  methods: {
    fetchData: async function () {
      const loyaltyProgram = await restaurantService.getRestaurant(
        localStorage.restaurantId,
        "loyaltyProgram"
      );
      this.loyaltyProgram = {
        ...this.loyaltyProgram,
        ...loyaltyProgram,
      };
      let customers = await customerService.getCustomers(
        localStorage.restaurantId
      );
      customers.forEach((customer) => {
        customer.ordersCount = customer.orderHistory[0].orders.length;
        customer.ordersSum =
          customer.orderHistory[0].orders.length === 1
            ? customer.orderHistory[0].orders[0].total
            : customer.orderHistory[0].orders
                .map((item) => item.total)
                .reduce((prev, next) => prev + next)
                .toFixed(2);

        customer.loyaltyCredits = customer.orderHistory[0].loyaltyCredits;

        customer.orderHistory[0].orders.forEach((order) => {
          order.created =
            parseInt(order.orderId.toString().substring(0, 8), 16) * 1000;
        });

        customer.lastOrdered =
          customer.orderHistory[0].orders.slice(-1)[0].created;
      });
      this.customers = customers;
      this.loading = false;
    },
    saveLoyaltyProgram: async function () {
      this.loyaltyProgramLoading = true;
      this.loyaltyProgram = await customerService.editLoyaltyProgram(
        localStorage.restaurantId,
        this.loyaltyProgram
      );
      this.dialog = false;
      this.loyaltyProgramLoading = false;
    },
    getPrettyDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);

      return this.isToday(date)
        ? date
            .toLocaleTimeString()
            .split(" ")[0]
            .replace(/:[0-9]*$/, "")
        : date.toLocaleDateString().replace(/ /g, "");
    },
    getPrettyDateFromObjectId(objectId) {
      const timestamp = objectId.toString().substring(0, 8);
      const date = new Date(parseInt(timestamp, 16) * 1000);

      return this.getPrettyDate(date);
    },
    isToday(date) {
      const today = new Date();
      return (
        date.getDate() == today.getDate() &&
        date.getMonth() == today.getMonth() &&
        date.getFullYear() == today.getFullYear()
      );
    },
    customFilter(value, search, item) {
      return (
        item.email.includes(this.searchValue) ||
        item.mobile.includes(this.searchValue) ||
        item.name.includes(this.searchValue)
      );
    },
  },
};
</script>

<style>
</style>
